import 'regenerator-runtime/runtime'
import * as React from 'react'
import { hydrate } from 'react-dom'
import { Provider } from 'react-redux'
import { Router } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import { loadableReady } from '@loadable/component'
import IntlProvider from '../shared/i18n/IntlProvider'
import rootSaga from '../shared/infra/rootEffects'
import configureStore from '../shared/infra/store'
import App from '../shared/App'
import ErrorBoundary from '../shared/components/ErrorBoundary'
import createHistory from '../shared/infra/history'
import { isMobileAgent } from '../shared/utils/userAgent'
import { registerCspViolationReporter } from '../shared/services/Tracking/cspViolationReporter'
import { getEnvConfig } from '../shared/utils/getEnvConfig'

// Report CSP violations to analytics as early as possible so runtime blocks
// (blocked scripts, or eval under a no-'unsafe-eval' policy) are observable
// instead of only surfacing in the console.
registerCspViolationReporter()

const history = createHistory()

const stateObj = configureStore({
  initialState: window.__PRELOADED_STATE__ || {}
})
// Create/use the store
// history MUST be passed here if you want syncing between server on initial route
const store = stateObj.store

// TODO(platform): Move the init code to a function like initiate()
if ('serviceWorker' in navigator && process.env.NODE_ENV !== 'development') {
  // Pass the Firebase config on the query string so the worker (which cannot
  // read window._env_) can init FCM for background messages. These values are
  // already public client config; nothing secret is exposed here.
  const swConfig = new URLSearchParams({
    apiKey: getEnvConfig('FIREBASE_API_KEY') || '',
    projectId: getEnvConfig('FIREBASE_PROJECT_ID') || '',
    messagingSenderId: getEnvConfig('FIREBASE_MESSAGING_SENDER_ID') || '',
    appId: getEnvConfig('FIREBASE_APP_ID') || ''
  }).toString()
  navigator.serviceWorker
    .register(`/firebase-messaging-sw.js?${swConfig}`, { scope: '/' })
    .then((reg) => {
      console.log('Service Worker running with registration' + reg.scope)
    })
}

const isMobileDevice = 'userAgent' in navigator ? isMobileAgent(navigator.userAgent) : false

stateObj.runSaga(rootSaga)

loadableReady(() => {
  hydrate(
    <Provider store={store}>
      <Router history={history}>
        <IntlProvider>
          <HelmetProvider>
            <ErrorBoundary>
              <App isMobileDevice={isMobileDevice} />
            </ErrorBoundary>
          </HelmetProvider>
        </IntlProvider>
      </Router>
    </Provider>,
    document.getElementById('app'),
    () => {
      // We don't need the static css any more once we have launched our application.
      const ssStyles = document.getElementById('server-side-styles')
      ssStyles?.parentNode?.removeChild(ssStyles)
    }
  )
})

if (process.env.NODE_ENV === 'development') {
  if (module.hot) {
    module.hot.accept()
  }

  if (!window.store) {
    window.store = store
  }
}
